import React, {forwardRef, useEffect, useImperativeHandle, useState} from "react";

import { Collapse } from "antd";

import { useTranslation } from "core/hooks/useTranslation";
import useDate from "core/hooks/useDate";

import Spin from "core/ui-kit/spin";
import Form from "core/ui-kit/form";

import Filters from "./filters";
import Details from "../details";

import { COMMISSION_ADD_FIELDS, COMMISSION_POPUP_MODE } from "../constants";

import commissionService from "core/services/apiServices/commissionService";

import OverlappedCommissions from "../overlappedCommissions";
import { formatDateRangePickerValues } from "../helpers";

const CommissionsPopupAdd = forwardRef(({
    affiliateId,
    onCommissionAssigned,
    setCreationDisabled,
    setIsFormTouched,
}, ref) => {

    const [formInstance] = Form.useForm();
    const {setFieldsValue} = formInstance;

    const {t} = useTranslation();
    const {dateService} = useDate();

    const [isLoading, setIsLoading] = useState(false);
    const [availableCommissions, setAvailableCommissions] = useState([]);

    const [selectedCommissionId, setSelectedCommissionId] = useState(null);

    const [detailsOpened, toggleDetails] = useState('');

    const [overlappedCommissions, setOverlappedCommissions] = useState([]);

    const selectedCommission = availableCommissions.find((item) => item.id === selectedCommissionId);

    const onAssignButtonClick = (values) => {
        const {fromValue, toValue} = formatDateRangePickerValues(values?.[COMMISSION_ADD_FIELDS.START_AND_END_DATE] || [], dateService);
        const params = {
            [COMMISSION_ADD_FIELDS.START_DATE]: fromValue,
            [COMMISSION_ADD_FIELDS.END_DATE]: toValue || null,
            [COMMISSION_ADD_FIELDS.COMMISSION]: values[COMMISSION_ADD_FIELDS.COMMISSION],
            affiliateId,
        };

        setIsLoading(true);
        commissionService.addCommission(params)
            .then(() => {
                onCommissionAssigned();
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const onSubmit = () => {
        formInstance.validateFields().then(onAssignButtonClick)
    }

    useImperativeHandle(ref, () => {
        return {
            onAssignButtonClick: () => {
                onSubmit()
            },
            canAssign: Boolean(selectedCommission)
        }
    }, [onAssignButtonClick, selectedCommission])

    const getInitialData = () => {
        setIsLoading(true);
        commissionService.getAffiliateAvailableCommissions(affiliateId)
            .then((affiliateAvailableCommissionsResponse) => {
                setAvailableCommissions(affiliateAvailableCommissionsResponse);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }

    const validationPassedAsValid = () => {
        setOverlappedCommissions([]);
        setCreationDisabled(false);
    }


    const validateConflicts = (currentValues) => {

        if(!currentValues[COMMISSION_ADD_FIELDS.COMMISSION] || !currentValues[COMMISSION_ADD_FIELDS.START_AND_END_DATE]?.[0]){
            return;
        }

        const commissionId = currentValues?.id;
        const {fromValue, toValue} = formatDateRangePickerValues(currentValues?.[COMMISSION_ADD_FIELDS.START_AND_END_DATE] || [], dateService);

        const params = {
            [COMMISSION_ADD_FIELDS.START_DATE]: fromValue,
            [COMMISSION_ADD_FIELDS.END_DATE]: toValue || null,
            [COMMISSION_ADD_FIELDS.COMMISSION]: commissionId,
            affiliateId,
            isEdit: false,
        };

        commissionService.validateCommissionAssignment(params)
            .then((response) => {
                const conflicts = response?.value || [];
                if (conflicts.length > 0) {
                    setCreationDisabled(true);
                    setOverlappedCommissions(conflicts);
                } else {
                    validationPassedAsValid();
                }
            });
    }

    const onFormValuesChange = (_, values) => {
        setSelectedCommissionId(values?.[COMMISSION_ADD_FIELDS.COMMISSION]);
        if (values?.[COMMISSION_ADD_FIELDS.COMMISSION] && values?.[COMMISSION_ADD_FIELDS.START_AND_END_DATE]?.[0]) {
            setIsFormTouched(true);
            validateConflicts(values);
        } else {
            setIsFormTouched(false);
            validationPassedAsValid();
        }
    }

    const clearOverlappedCommissionsErrors = () => {
        const now = new Date();
        const oneHourLater = new Date(now.getTime() + 60 * 60 * 1000);

        setFieldsValue({
            [COMMISSION_ADD_FIELDS.COMMISSION]: null, 
            [COMMISSION_ADD_FIELDS.START_AND_END_DATE]: [dateService.makeUtcDateToLocal(oneHourLater), null]
        });
        setOverlappedCommissions([]);
        setCreationDisabled(false);
        toggleDetails('');
        setIsFormTouched(false);
        setSelectedCommissionId(null);
    }

    useEffect(() => {
        getInitialData();
    }, []);

    const hideDetails = () => toggleDetails("")

    return (
        <div className="rt--commissions-add">
            {
                overlappedCommissions.length > 0 && (
                    <OverlappedCommissions
                        overlappedCommissions={overlappedCommissions}
                        clearOverlappedCommissionsErrors={clearOverlappedCommissionsErrors}
                    />
                )
            }
            <Filters
                formInstance={formInstance}
                hideDetails={hideDetails}
                onFormValuesChange={onFormValuesChange}
                availableCommissions={availableCommissions}
            />
            {
                isLoading ? (
                    <div className="rt--loading-wrapper">
                        <Spin/>
                    </div>
                ) : (
                    <div className="rt--dropdown-row">
                        <Collapse
                            className="rt--collapse rt--collapse-totals"
                            activeKey={detailsOpened} 
                            onChange={(key) => toggleDetails(key)}
                        >
                            <Collapse.Panel
                                showArrow={true}
                                collapsible={!selectedCommission ? 'disabled' : undefined}
                                header={(
                                    <div className="rt--dropdown-visible-area">
                                        {t('backoffice.commissionplans.commissionDetails')}
                                    </div>
                                )}
                                key="1"
                            >
                                <Details
                                    commission={selectedCommission}
                                    mode={COMMISSION_POPUP_MODE.ADD}
                                />
                            </Collapse.Panel>
                        </Collapse>
                    </div>
                )
            }
        </div>
    )
})

export default CommissionsPopupAdd;